import { v2ParamsSerializer } from '@stuut/common/src/api_client';
import { Invoice } from '../models';
import { ModelResource } from './resource';

export interface InvoiceSearchResult {
  invoice: Invoice;
  score: number;
  match_type: 'exact' | 'fuzzy' | null;
  matched_field: string | null;
  matched_value: string | null;
  debug_info?: Record<string, any> | null;
}

export interface InvoiceSearchResponse {
  data: InvoiceSearchResult[];
  total_records: number;
  current_page: number;
  next_page: number | null;
}

/** @deprecated Use InvoiceSearchParamsV2 instead */
export interface InvoiceSearchParams {
  query?: string;
  limit?: number;
  page?: number;
  amount?: number;
  status?: string[];
  assignee?: string;
  partners?: string[];
  min_score?: number;
  currency?: string;
}

/**
 * V2 search params with Decimal amount support and __in filter naming.
 * Amount is in standard units (e.g., 10.25 for $10.25 USD).
 */
export interface InvoiceSearchParamsV2 {
  query?: string;
  limit?: number;
  page?: number;
  /** Amount in standard units (e.g., 10.25 for $10.25 USD) */
  amount?: number;
  currency?: string;
  status__in?: string[];
  partner_ids__in?: string[];
  min_score?: number;
}

export class Invoices extends ModelResource<Invoice> {
  model = Invoice;

  resourcePath = 'v1/invoices';

  async invoiceByIds(number: string[]): Promise<Invoice[]> {
    return super.list({ params: { number } });
  }

  async sync(invoice: Invoice): Promise<Invoice> {
    const res = await this.api.post(this.path(invoice.id, 'sync'));

    invoice.updateFromJson(res.data);

    return invoice;
  }

  /**
   * Search invoices using v2 API with Decimal amount support.
   * Amount is in standard units (e.g., 10.25 for $10.25 USD).
   */
  async search(params: InvoiceSearchParamsV2): Promise<InvoiceSearchResponse> {
    const res = await this.api.get('v2/invoices/search', {
      params,
      paramsSerializer: v2ParamsSerializer,
    });

    // Deserialize invoices in data
    const data = res.data.data.map((result: any) => ({
      ...result,
      invoice: this.model.fromJson(result.invoice) as Invoice,
    }));

    return {
      ...res.data,
      data,
    };
  }

  async bulkUpdatePromisedToPayDate(
    invoiceIds: string[],
    promisedToPayDate: string | null,
  ): Promise<{ updated_count: number }> {
    const res = await this.api.post('v2/invoices/bulk/promised-to-pay-date', {
      data: {
        invoice_ids: invoiceIds,
        promised_to_pay_date: promisedToPayDate,
      },
    });
    return res.data;
  }

  /**
   * @deprecated Use search() instead, which now uses v2 API
   */
  async searchV1(params: InvoiceSearchParams): Promise<InvoiceSearchResponse> {
    const res = await this.api.get(this.path('search'), { params });

    // Deserialize invoices in data
    const data = res.data.data.map((result: any) => ({
      ...result,
      invoice: this.model.fromJson(result.invoice) as Invoice,
    }));

    return {
      ...res.data,
      data,
    };
  }
}
